import { useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import StudioEditor from '@grapesjs/studio-sdk/react';
import '@grapesjs/studio-sdk/style';
import { Button, Spin } from 'components-antd';
import { useHistory, useParams } from 'react-router-dom';
import { useKitsTemplates } from 'pages/Dashboard/Kits/components/KitsTemplate/use-kit-templates.hook';
import { showErrorMessage } from 'helpers';
import {
  STARTER_TEMPLATE,
  initialCustomData,
} from 'pages/Dashboard/Kits/components/KitsTemplate/helpers';
import { registerHandlebarsBlocks } from './utils';

type KitsTemplatePropsType = {};

function KitsTemplate(props: KitsTemplatePropsType) {
  const history = useHistory();
  const editorRef = useRef<any>(null);
  const params = useParams<{ kitId: string }>();
  const kitId = params.kitId;

  const {
    error,
    loading,
    isSaving,
    isDeleting,
    selectedKit,
    existingTemplate,
    fetchKitsData,
    fetchTemplateByKitId,
    saveTemplate,
    deleteTemplate,
  } = useKitsTemplates(kitId);

  useEffect(() => {
    async function fetchData() {
      await fetchTemplateByKitId();
      await fetchKitsData();
    }
    void fetchData();
  }, [fetchKitsData, fetchTemplateByKitId]);

  useEffect(() => {
    if (error) {
      showErrorMessage(error);
    }
  }, [error]);

  const getSafeHTml = (html: string) => {
    return html.replace(/&(?!(amp|lt|gt|quot|apos|#\d+);)/g, '&amp;');
  };

  const resetTemplate = () => {
    if (editorRef.current) {
      editorRef.current.setComponents(STARTER_TEMPLATE);
    }
  };

  const handleSave = async () => {
    if (editorRef.current) {
      await saveTemplate(editorRef.current);
    }
  };

  const handleDelete = async () => {
    if (existingTemplate) {
      await deleteTemplate();
      resetTemplate();
    }
  };

  return (
    <>
      <div testid="header" className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            variant={'go-back'}
            onClick={() => {
              history.push('/dashboard/kits');
            }}
            className={styles.goBackButton}
          />
        </div>
        <div>{loading ? 'Loading...' : `${selectedKit?.Name || 'Kit'} Email Template`}</div>
      </div>

      <div className={styles.template}>
        {loading ? (
          <Spin size="large" className={styles.loader} />
        ) : (
          <StudioEditor
            onReady={(studio) => {
              editorRef.current = studio.editor;
              if (existingTemplate?.html) {
                editorRef.current.setComponents(getSafeHTml(existingTemplate.html));
              } else if (existingTemplate?.templateMeta?.projectJSON) {
                editorRef.current.loadProject(existingTemplate.templateMeta.projectJSON);
              } else {
                // Set header and footer as uneditable
                editorRef.current.setComponents(STARTER_TEMPLATE);
              }
            }}
            options={{
              licenseKey: process.env.REACT_APP_GRAPEJS_LICENSE_KEY,
              project: {
                type: 'email',
                id: `kit-${kitId}`,
              },
              i18n: {
                locales: {
                  en: {
                    actions: {
                      importCode: {
                        content: 'Paste here your MJML code and click Import',
                      },
                    },
                  },
                },
              },
              identity: {
                id: 'user-1',
              },
              assets: {
                storageType: 'self',
              },
              storage: {
                type: 'self',
                // autosaveChanges: 5,
                // autosaveIntervalMs: 10000,
                // project: projectJSON,
                // onSave: async ({ project }) => {
                // },
              },
              // Initialize with default template variables
              customData: initialCustomData,
              plugins: [
                (editor) => {
                  // Register a new component type and Handlebars blocks
                  registerHandlebarsBlocks(editor);
                },
              ],
            }}
          />
        )}
      </div>
      <div className={styles.btnWrapper}>
        <div className={styles.saveButtonWrapper}>
          <Button variant="secondary" onClick={handleSave} disabled={loading} loading={isSaving}>
            Save Template
          </Button>
        </div>
        {existingTemplate && (
          <div className={styles.deleteButtonWrapper}>
            <Button
              variant={'primary'}
              onClick={handleDelete}
              disabled={loading}
              loading={isDeleting}
            >
              Delete Template
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default KitsTemplate;
